import { useRef } from "react";
import { Eye, Heart } from "lucide-react";
import { useVideoSource } from "~/lib/hooks/useVideoSource";
import { formatCount } from "~/lib/utils";

export interface EntertainmentVideoCardData {
  feedItemId: string;
  videoUrl: string;
  /** Poster frame. Without it an HLS card is an empty box until the pointer arrives, because
   *  nothing is fetched before then. */
  thumbnail?: string;
  viewCount?: number;
  likeCount?: number;
}

interface EntertainmentVideoCardProps {
  data: EntertainmentVideoCardData;
  onClick?: () => void;
}

export function EntertainmentVideoCard({ data, onClick }: EntertainmentVideoCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);
  const { startLoad, stopLoad } = useVideoSource(videoRef, data.videoUrl);

  const handleMouseEnter = () => {
    const video = videoRef.current;
    if (!video) return;
    // HLS only starts fetching here. A row of these renders every item at once, and each hls.js
    // instance carries its own MediaSource and first fragment, so paying for that on cards nobody
    // hovers would cost more than the preview is worth.
    startLoad();
    const promise = video.play();
    playPromiseRef.current = promise;
    promise.catch(e => {
      if (e.name !== "AbortError") console.error("Video play failed:", e);
    });
  };

  const handleMouseLeave = () => {
    const video = videoRef.current;
    if (!video) return;
    // Give the player back rather than leaving one per card the pointer has crossed. Re-hovering
    // refetches a first fragment the CDN already has, which is cheaper than the memory.
    stopLoad();
    const p = playPromiseRef.current;
    if (p) {
      p.then(() => {
        video && !video.paused && video.pause();
      })
        .catch(() => {})
        .finally(() => {
          playPromiseRef.current = null;
        });
    } else if (!video.paused) {
      video.pause();
    }
  };

  return (
    <div
      id={`watch-entertainment-video-card-${data.feedItemId}`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className="w-full cursor-pointer overflow-hidden rounded-xl"
    >
      {/* Thumbnail */}
      <div
        className="relative aspect-[9/16] bg-neutral-900"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <video
          ref={videoRef}
          poster={data.thumbnail}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      {/* Stats only — no title */}
      <div className="flex items-center gap-1.5 px-2 pt-2 text-neutral-500">
        {data.viewCount !== undefined && (
          <>
            <Eye className="h-3 w-3" />
            <span className="text-[11px]">{formatCount(data.viewCount)} views</span>
          </>
        )}
        {data.viewCount !== undefined && data.likeCount !== undefined && (
          <span className="text-[11px] text-neutral-300">·</span>
        )}
        {data.likeCount !== undefined && (
          <>
            <Heart className="h-3 w-3" />
            <span className="text-[11px]">{formatCount(data.likeCount)} likes</span>
          </>
        )}
      </div>
    </div>
  );
}
